import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Plus } from "lucide-react";
import t from "@/lang/lang";
import { useEffect, useState } from "react";
import {
  CreateRequest,
  useRelationshipsRelationshipCreateMutation,
} from "@/services/backend/relationships/api/service";
import { v4 } from "uuid";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { CompanySelect } from "@/services/backend/company/components/company-select";
import { Label } from "@/shared/components/ui/label";
import {
  inputValidationErrorMessages,
  InputValidationErrors,
} from "@/shared/components/errors/input-error-messages";
import { toast } from "sonner";
import { BranchSelect } from "@/services/backend/company/components/branch-select";
import { DateInput } from "@/shared/components/form/date-input";
import { CustomerSelect } from "@/services/backend/customers/components/customer-select";
import { ServiceObjectSelect } from "@/services/backend/customers/components/service-object-select";
import {
  CommonDialogFooter,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";

export interface CreateRelationshipDialogProps {
  companyId?: string;
  branchId?: string;
  customerId?: string;
  serviceObjectId?: string;
}

export function CreateRelationshipDialog({
  companyId,
  branchId,
  customerId,
  serviceObjectId,
}: CreateRelationshipDialogProps) {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState<CreateRequest>(
    newRequest(companyId, branchId, customerId, serviceObjectId),
  );

  const [put, { isLoading, error, isSuccess, reset }] =
    useRelationshipsRelationshipCreateMutation();

  useEffect(() => {
    if (isSuccess) {
      setOpen(false);
      setRequest(newRequest(companyId, branchId, customerId, serviceObjectId));
      reset();
      toast("Beziehung erstellt");
    }
  }, [branchId, companyId, customerId, isSuccess, reset, serviceObjectId]);

  const doPut = () => {
    if (isLoading) {
      return;
    }

    put(request);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <CommonDialogTrigger icon={Plus} label={t("Beziehung")} size="sm" />
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Beziehung erstellen")}</DialogTitle>
          <DialogDescription>
            {t("Hier kann eine Beziehung erstellt werden.")}
          </DialogDescription>
        </DialogHeader>
        <Fields request={request} onRequestChange={setRequest} error={error} />
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={doPut}
          actionLabel={t("Erstellen")}
        />
      </DialogContent>
    </Dialog>
  );
}

function Fields({
  request,
  onRequestChange,
  error,
}: {
  request: CreateRequest;
  onRequestChange: (request: CreateRequest) => void;
  error: unknown;
}) {
  return (
    <div className="grid gap-4">
      <div className="grid gap-1.5">
        <Label>{t("Unternehmen")}</Label>
        <CompanySelect
          companyId={request.companyId}
          onCompanyIdChange={(companyId) =>
            onRequestChange({ ...request, companyId })
          }
        />
        <InputValidationErrors error={error} field="companyId" />
      </div>
      <div className="grid gap-1.5">
        <Label>{t("Niederlassung")}</Label>
        <BranchSelect
          value={request.branchId}
          onValueChange={(branchId) =>
            onRequestChange({ ...request, branchId })
          }
          companyId={request.companyId}
        />
        <InputValidationErrors error={error} field="branchId" />
      </div>
      <div className="grid gap-1.5">
        <Label>{t("Kunde")}</Label>
        <CustomerSelect
          value={request.customerId}
          onValueChange={(customerId) =>
            onRequestChange({ ...request, customerId })
          }
        />
        <InputValidationErrors error={error} field="customerId" />
      </div>
      <div className="grid gap-1.5">
        <Label>{t("Serviceobjekt")}</Label>
        <ServiceObjectSelect
          value={request.serviceObjectId}
          onValueChange={(serviceObjectId) =>
            onRequestChange({ ...request, serviceObjectId })
          }
          customerId={request.customerId}
        />
        <InputValidationErrors error={error} field="serviceObjectId" />
      </div>
      <DateInput
        label={t("Start")}
        date={request.start}
        onDateChange={(start) => {
          if (start) {
            onRequestChange({ ...request, start });
          }
        }}
        errors={inputValidationErrorMessages(error, "start")}
      />
      <DateInput
        label={t("Ende")}
        date={request.end}
        onDateChange={(end) => {
          if (end) {
            onRequestChange({ ...request, end });
          }
        }}
        errors={inputValidationErrorMessages(error, "end")}
      />
    </div>
  );
}

function newRequest(
  companyId?: string,
  branchId?: string,
  customerId?: string,
  serviceObjectId?: string,
): CreateRequest {
  const now = new Date();
  return {
    id: v4(),
    companyId: companyId ?? "",
    branchId: branchId ?? "",
    customerId: customerId ?? "",
    serviceObjectId: serviceObjectId ?? "",
    start: now.toISOString(),
    end: new Date(new Date().setFullYear(now.getFullYear() + 5)).toISOString(),
  };
}
